import React, { useEffect, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { getContactInfo, sendContactMessage } from "../services/contactService";

export default function Contact() {
  const [contactInfo, setContactInfo] = useState({
    email: "",
    phone: "",
    address: "",
    whatsapp: "",
  });
  const [loadingInfo, setLoadingInfo] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // ✅ Fetch contact info on load
  useEffect(() => {
    document.title = "Contact | Development Solutions";

    const fetchContact = async () => {
      try {
        const data = await getContactInfo();
        setContactInfo({
          email: data.email || "info@devsols.com",
          phone: data.phone || "+92 300 1234567",
          address: data.address || "Dev Sols HQ, Lahore, Pakistan",
          whatsapp: data.whatsapp || "+92 300 7654321",
        });
      } catch (error) {
        console.error("❌ Failed to load contact info:", error);
        // ✅ fallback in case API fails
        setContactInfo({
          email: "info@devsols.com",
          phone: "+92 300 1234567",
          address: "Dev Sols HQ, Lahore, Pakistan",
          whatsapp: "+92 300 7654321",
        });
      } finally {
        setLoadingInfo(false);
      }
    };

    fetchContact();
  }, []);

  // ✅ Handle form input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ✅ Submit the contact form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setSuccessMsg("");
    setErrorMsg("");

    try {
      await sendContactMessage(formData);
      setSuccessMsg("✅ Your message has been sent successfully!");
      setFormData({ name: "", email: "", message: "" }); // reset form
    } catch (err) {
      console.error(err);
      setErrorMsg("❌ Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* ✅ HERO SECTION */}
      <section
        className="relative h-[50vh] flex items-center justify-center text-center"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/3184357/pexels-photo-3184357.jpeg?auto=compress&cs=tinysrgb&w=1600')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 px-4 sm:px-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
            Contact Us
          </h1>
          <p className="mt-4 text-sm sm:text-base lg:text-lg text-gray-200 max-w-2xl mx-auto">
            Let's build something amazing together. Reach out anytime.
          </p>
        </div>
      </section>

      {/* ✅ CONTACT DETAILS + FORM */}
      <section className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[90vh] flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center justify-center">
          {/* LEFT: CONTACT INFO */}
          <div className="w-full order-2 lg:order-1">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 text-gray-800 text-center lg:text-left">
              Get in <span className="text-[#1b75bc]">Touch</span>
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 text-center lg:text-left">
              Whether you have a project in mind, a question about our services,
              or just want to say hello, we'd love to hear from you.
            </p>

            <div className="space-y-4 sm:space-y-6">
              {/* Address */}
              <div className="flex items-start">
                <span className="text-lg sm:text-xl lg:text-2xl mr-3 sm:mr-4 text-[#1b75bc] flex-shrink-0">
                  📍
                </span>
                <div>
                  <h4 className="font-bold text-sm sm:text-base">Address</h4>
                  {loadingInfo ? (
                    <p className="text-gray-400 animate-pulse text-xs sm:text-sm">
                      Loading...
                    </p>
                  ) : (
                    <p className="text-gray-600 text-xs sm:text-sm">
                      {contactInfo.address}
                    </p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start">
                <span className="text-lg sm:text-xl lg:text-2xl mr-3 sm:mr-4 text-[#1b75bc] flex-shrink-0">
                  📧
                </span>
                <div>
                  <h4 className="font-bold text-sm sm:text-base">Email</h4>
                  {loadingInfo ? (
                    <p className="text-gray-400 animate-pulse text-xs sm:text-sm">
                      Loading...
                    </p>
                  ) : (
                    <p className="text-gray-600 text-xs sm:text-sm break-all">
                      {contactInfo.email}
                    </p>
                  )}
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start">
                <span className="text-lg sm:text-xl lg:text-2xl mr-3 sm:mr-4 text-[#1b75bc] flex-shrink-0">
                  📞
                </span>
                <div>
                  <h4 className="font-bold text-sm sm:text-base">Phone</h4>
                  {loadingInfo ? (
                    <p className="text-gray-400 animate-pulse text-xs sm:text-sm">
                      Loading...
                    </p>
                  ) : (
                    <p className="text-gray-600 text-xs sm:text-sm">
                      {contactInfo.phone}
                    </p>
                  )}
                </div>
              </div>

              {/* WhatsApp */}
              <div className="flex items-start">
                <FaWhatsapp className="text-green-600 text-lg sm:text-xl lg:text-2xl mr-3 sm:mr-4 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-sm sm:text-base">WhatsApp</h4>
                  {loadingInfo ? (
                    <p className="text-gray-400 animate-pulse text-xs sm:text-sm">
                      Loading...
                    </p>
                  ) : (
                    <a
                      href={`https://wa.me/${contactInfo.whatsapp.replace(
                        /\D/g,
                        ""
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:underline text-xs sm:text-sm"
                    >
                      {contactInfo.whatsapp}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: CONTACT FORM */}
          <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-md w-full order-1 lg:order-2">
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-4 sm:mb-6 text-gray-800 text-center lg:text-left">
              Send us a <span className="text-[#1b75bc]">Message</span>
            </h3>

            {successMsg && (
              <p className="mb-4 text-green-600 font-semibold text-xs sm:text-sm">
                {successMsg}
              </p>
            )}
            {errorMsg && (
              <p className="mb-4 text-red-600 font-semibold text-xs sm:text-sm">
                {errorMsg}
              </p>
            )}

            <form className="space-y-3 sm:space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                  Your Name
                </label>
                <input
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  required
                  className="w-full mt-1 p-2 sm:p-3 border rounded-md focus:ring-2 focus:ring-[#1b75bc] text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                  className="w-full mt-1 p-2 sm:p-3 border rounded-md focus:ring-2 focus:ring-[#1b75bc] text-sm"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  name="message"
                  rows="3"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Write your message..."
                  required
                  className="w-full mt-1 p-2 sm:p-3 border rounded-md focus:ring-2 focus:ring-[#1b75bc] text-sm resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={sending}
                className={`w-full py-2 sm:py-3 rounded-md text-white font-medium hover:opacity-90 transition text-sm sm:text-base ${
                  sending ? "bg-blue-400 cursor-not-allowed" : "bg-[#1b75bc]"
                }`}
              >
                {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
